export interface PriceFilterValue {
  min: number | null;
  max: number | null;
}

export interface PriceFilterChange {
  min: number;
  max: number;
  currency: string;
}

export interface PriceFilterProps {
  currency: string;
  currencyRates: { [key: string]: number };
  trimmedPriceDistribution: Array<{ range: number; value: number }>;
  trimmedPriceMax: number;
  trimmedPriceMin: number;
  priceMax: number;
  priceMin: number;
  selectedMin: number;
  selectedMax: number;
}

export enum TypeValue {
  PRIVATE_ROOM = 'private-rooms',
  SHARED_ROOM = 'shared-rooms',
  APARTMENT = 'apartment-for-rent',
  STUDIO = 'studio-for-rent',
}
export type TypeFilterValue = TypeValue[];
export type PropertyTypeFilterCount = {
  studio?: number;
  apartment?: number;
  privateRoom?: number;
  sharedRoom?: number;
};

export type DateString = string | null;

export interface DateFilterValue {
  startDate: DateString;
  endDate: DateString;
}

export enum FurnitureValue {
  FURNISHED = 'furnished',
  UNFURNISHED = 'unfurnished',
}
export type FurnitureFilterValue = FurnitureValue[];
export type FurnitureFilterCount = {
  furnished?: number;
  unfurnished?: number;
};

export enum BillsValue {
  INCLUDED = 'included',
  EXCLUDED = 'excluded',
}
export type BillsFilterValue = BillsValue[];
export type BillsFiltersCount = {
  included?: number;
};

export enum GenderValue {
  GIRLS = 'female',
  BOYS = 'male',
}
export type GenderFilterValue = GenderValue[];
export type GenderFilterCount = {
  female?: number;
  male?: number;
};

export type RegistrationValue = 'possible' | 'not-possible';
export type RegistrationFilterValue = RegistrationValue[];
export type RegistrationFilterCount = {
  possible?: number;
};

export enum Options {
  NO = 'no',
  YES = 'yes',
  DISCUSSABLE = 'discussable',
  OUTSIDE = 'outside_only',
}

export enum RulesValue {
  SMOKING = 'smoking_allowed',
  PETS = 'pets_allowed',
  INSTRUMENT = 'playing_instrument_allowed',
}
export type RulesFilterValue = RulesValue[];

export type PropertySizeFilterStats = {
  minValue?: number;
  maxValue?: number;
};
export type PropertySizeFilterValue = {
  sizeMin?: number;
  sizeMax?: number;
};
export type PropertySizeFilterCount = {
  over15?: number;
  over30?: number;
  over60?: number;
  over90?: number;
};

export enum RoomsValue {
  ONE = '1',
  TWO = '2',
  THREE = '3',
  FOUR_AND_MORE = '4+',
}
export type RoomsFilterProperty = 'bedroomCount';
export interface RoomsFilterValue {
  bedroomCount: RoomsValue[];
}
export type RoomsFilterCount = {
  one?: number;
  two?: number;
  three?: number;
  overFour?: number;
};

export type RecentlyAddedValue =
  | 'all'
  | '1-day'
  | '3-days'
  | '7-days'
  | '14-days';
export type RecentlyAddedFilterValue = RecentlyAddedValue | null;

export enum SuitableForValue {
  STUDENTS = 'students',
  WORKING_PROFESSIONALS = 'working-professionals',
  COUPLES = 'couples',
}
export type SuitableForFilterValue = SuitableForValue[];
export type SuitableForFilterCount = {
  students?: number;
  workingProfessionals?: number;
  couples?: number;
};

export type CanonicalCollection =
  | TypeValue
  | 'cheap-accommodation'
  | 'furnished-apartments'
  | '';

export enum SortingValue {
  BestMatch = 'bestMatch',
  MostRecent = 'mostRecent',
  LowToHigh = 'lowToHigh',
  HighToLow = 'highToLow',
}

export enum ContractTypeValue {
  Daily = 'daily',
  Fortnightly = 'fortnightly',
  Monthly = 'monthly',
  Unknown = 'unknown',
}
export type ContractTypeFilterValue = ContractTypeValue[];
export type ContractTypeFilterCount = { [key in ContractTypeValue]?: number };

export type SortingOption = Array<{ key: string; value: SortingValue }>;

export enum SearchAreaValue {
  MapBounds = 'map',
  CityBounds = 'city',
}
export type SearchAreaFilterValue = SearchAreaValue[];

export enum FacilitiesValue {
  PRIVATE_BATHROOM = 'private_bathroom',
  BALCONY_TERRACE = 'balcony_terrace',
  GARDEN = 'garden',
  KITCHEN = 'kitchen',
  PET_FRIENDLY = 'pets_friendly',
  PARKING = 'parking',
  WHEELCHAIR_ACCESSIBLE = 'wheelchair_accessible',
  BASEMENT = 'basement',
}
export type FacilitiesFilterValue = FacilitiesValue[];
export type FacilitiesFilterCount = {
  privateBathroom?: number;
  balconyTerrance?: number;
  garden?: number;
  kitchen?: number;
  petsFriendly?: number;
  parking?: number;
  wheelchairAccessible?: number;
  basement?: number;
};

export enum AmenitiesValue {
  TV = 'tv',
  DESK = 'desk',
  DISHWASHER = 'dishwasher',
  WASHING_MACHINE = 'washing_machine',
  DRYER = 'dryer',
  AIR_CONDITIONING = 'ac',
  HEATING = 'heating',
}
export type AmenitiesFilterValue = AmenitiesValue[];
export type AmenitiesFilterCount = {
  tv?: number;
  desk?: number;
  dishwasher?: number;
  washingMachine?: number;
  dryer?: number;
  airConditioning?: number;
  heating?: number;
};

export enum RadiusValue {
  DEFAULT = 'default',
  R1 = '1km',
  R5 = '5km',
  R10 = '10km',
  R15 = '15km',
  R20 = '20km',
}
export type RadiusFilterValue = RadiusValue;

export function radiusValueToMeters(radius: RadiusValue) {
  if (radius === RadiusValue.DEFAULT) {
    return 0;
  }

  return Number(radius.split('km')[0]) * 1000;
}

export interface Filters {
  dates?: DateFilterValue;
  types?: TypeFilterValue;
  furniture?: FurnitureFilterValue;
  bills?: BillsFilterValue;
  rooms?: RoomsFilterValue;
  rules?: RulesFilterValue;
  price?: PriceFilterValue;
  currency?: string;
  registration?: RegistrationFilterValue;
  recentlyAdded?: RecentlyAddedFilterValue;
  searchArea?: SearchAreaFilterValue;
  gender?: GenderFilterValue;
  suitableFor?: SuitableForFilterValue;
  contractType?: ContractTypeFilterValue;
  sorting?: SortingValue;
  propertySize?: PropertySizeFilterValue;
  facilities?: FacilitiesFilterValue;
  amenities?: AmenitiesFilterValue;
  radius?: RadiusFilterValue;
  place?: string;
}

export type FilterSubmit = (filters: Filters) => void;
